import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import {
  Modal,
  ModalControlModalProps,
  RadioField,
  useFormField,
  Text,
  CheckboxField,
  Button,
  useModalLoadingState,
} from '@frontend/design-system';
import { TermsAndConditionsBody } from './terms-and-conditions-body';

const styles = {
  termsAndConditions: css`
    height: 200px; /* Set your desired height */
    width: auto;
    overflow-y: auto;
    overflow-x: auto;
    margin-bottom: ${theme.spacing(2)};
    @media (min-width: ${breakpoints.medium.min}px) {
      padding: ${theme.spacing(0, 2)};
    }
    /* Customize scrollbar for better appearance */
    &::-webkit-scrollbar {
      width: 8px;
    }
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: #a8a8a8;
    }
  `,
  radioField: css`
    min-width: min-content;
    max-width: 300px;
  `,
  checkboxField: css`
    width: 100%;
    margin-bottom: ${theme.spacing(2)};
    @media (min-width: ${breakpoints.medium.min}px) {
      align-items: center;
      text-wrap: wrap;
      width: 50%;
    }
  `,
  footerActions: css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    @media (min-width: ${breakpoints.medium.min}px) {
      flex-direction: row;
    }
  `,
};
interface StripeSignupModalProps {
  onCreateAccount: ({
    businessType,
    setLoading,
  }: {
    businessType: string;
    setLoading?: (loading: boolean) => void;
  }) => void;
  modalProps: ModalControlModalProps;
}

export const StripeSignupModalBody = ({ onCreateAccount, modalProps }: StripeSignupModalProps) => {
  const { t } = useTranslation('payments');
  const radioFieldProps = useFormField({ type: 'radio', required: true });
  const checkboxFieldProps = useFormField({ type: 'checkbox' });
  const { setLoading } = useModalLoadingState();

  return (
    <>
      <Modal.Header onClose={modalProps.onClose}>{t('Welcome to Weave Payments!')}</Modal.Header>
      <Modal.Body>
        <Text size='medium'>
          {t("To get started, you'll need to create a Stripe account and select your business type below.")}
        </Text>
        <Text weight='bold'>{t('Type of Business')}</Text>
        <RadioField
          name='business_type'
          {...radioFieldProps}
          css={styles.radioField}
          data-trackingid={'portal-payments_setup-stripe_type-of-business_radio'}
        >
          <RadioField.Radio value='COMPANY'>{t('Business')}</RadioField.Radio>
          <RadioField.Radio value='NON_PROFIT'>{t('Nonprofit Organization')}</RadioField.Radio>
          <RadioField.Radio value='INDIVIDUAL'>{t('Individual')}</RadioField.Radio>
        </RadioField>
        <Text size='medium'>{t('Please read and agree to our terms and conditions to proceed.')}</Text>
        <Text weight='bold'>{t('Terms and Conditions')}</Text>
        <div css={styles.termsAndConditions}>
          <TermsAndConditionsBody />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div css={styles.footerActions}>
          <CheckboxField
            {...checkboxFieldProps}
            disabled={!radioFieldProps.value}
            name='agrees'
            trackingId='portal-payments_setup-stripe_tos_checkbox'
            label={t('I have read and agree with the Terms and Conditions')}
            css={styles.checkboxField}
          />
          <Button
            variant='primary'
            disabled={!checkboxFieldProps.value || !radioFieldProps.value}
            onClick={() => onCreateAccount({ businessType: radioFieldProps.value, setLoading })}
            trackingId='portal-payments_setup-stripe_create-account_button'
          >
            {t('Setup Account')}
          </Button>
        </div>
      </Modal.Footer>
    </>
  );
};

export const StripeSignupModal = ({ onCreateAccount, modalProps }: StripeSignupModalProps) => {
  return (
    <Modal {...modalProps} minWidth={500} maxWidth={700}>
      <StripeSignupModalBody onCreateAccount={onCreateAccount} modalProps={modalProps} />
    </Modal>
  );
};
